import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import Badge from '@/components/base/Badge';

interface Renovacion {
  id: number;
  cliente: string;
  servicio: string;
  productoSku: string;
  fechaRenovacion: string;
}

export default function UpcomingRenewals() {
  const [renovaciones, setRenovaciones] = useState<Renovacion[]>([]);
  const [loading, setLoading] = useState(true);

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 6 }).format(val);

  useEffect(() => {
    fetchRenovaciones();
  }, []);

  async function fetchRenovaciones() {
    try {
      const { data: subs, error: subErr } = await supabase
        .from('erp_subscriptions')
        .select('id, client_id, product_id, start_date')
        .eq('status', 'active')
        .order('start_date', { ascending: true });

      if (subErr) throw subErr;
      if (!subs || subs.length === 0) { setLoading(false); return; }

      const clientIds = [...new Set(subs.map(s => s.client_id))];
      const productIds = [...new Set(subs.map(s => s.product_id))];

      const [clientsRes, prodsRes] = await Promise.all([
        supabase.from('erp_clients').select('id, company_name').in('id', clientIds),
        supabase.from('erp_products').select('id, sku, name, unit_price').in('id', productIds),
      ]);

      const clientMap = new Map((clientsRes.data || []).map(c => [c.id, c.company_name]));
      const prodMap = new Map((prodsRes.data || []).map(p => [p.id, p]));

      const hoy = new Date('2026-08-07');
      const en7Dias = new Date('2026-08-14');

      const renovs: Renovacion[] = subs
        .map(s => {
          const fechaStart = new Date(s.start_date);
          const diaRenovacion = fechaStart.getDate();
          const mesRenovacion = hoy.getMonth();
          const anioRenovacion = hoy.getFullYear();
          const fechaCalculada = new Date(anioRenovacion, mesRenovacion, diaRenovacion);
          if (fechaCalculada < hoy) {
            fechaCalculada.setMonth(fechaCalculada.getMonth() + 1);
          }
          return { ...s, fechaRenovacion: fechaCalculada };
        })
        .filter(s => s.fechaRenovacion >= hoy && s.fechaRenovacion <= en7Dias)
        .sort((a, b) => a.fechaRenovacion.getTime() - b.fechaRenovacion.getTime())
        .map(s => ({
          id: s.id,
          cliente: clientMap.get(s.client_id) || 'Cliente',
          servicio: prodMap.get(s.product_id)?.name || 'Servicio',
          productoSku: prodMap.get(s.product_id)?.sku || '',
          fechaRenovacion: s.fechaRenovacion.toISOString().split('T')[0],
        }));

      setRenovaciones(renovs);
    } catch (err) {
      console.error('Error fetching renewals:', err);
    } finally {
      setLoading(false);
    }
  }

  const getDiasRestantes = (fecha: string) => {
    const hoy = new Date('2026-08-07');
    const renovacion = new Date(fecha + 'T00:00:00');
    return Math.ceil((renovacion.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-background-200 p-6">
        <div className="flex items-center justify-center h-32">
          <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-background-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
            <i className="ri-refresh-line text-amber-500 text-sm"></i>
          </div>
          <h3 className="text-sm font-semibold text-foreground-900">Próximas Renovaciones</h3>
        </div>
        <button className="text-xs font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer whitespace-nowrap">
          <i className="ri-download-2-line"></i>
          Exportar
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-background-100 bg-background-50">
              <th className="text-left px-5 py-3 font-semibold text-foreground-500">Cliente</th>
              <th className="text-left px-5 py-3 font-semibold text-foreground-500">Servicio</th>
              <th className="text-center px-5 py-3 font-semibold text-foreground-500">Renovación</th>
              <th className="text-center px-5 py-3 font-semibold text-foreground-500">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-background-100">
            {renovaciones.map((ren) => {
              const dias = getDiasRestantes(ren.fechaRenovacion);
              return (
                <tr key={ren.id} className="hover:bg-background-50 transition-colors">
                  <td className="px-5 py-3">
                    <span className="font-medium text-foreground-800">{ren.cliente}</span>
                    <span className="block text-[10px] text-foreground-500">{ren.productoSku}</span>
                  </td>
                  <td className="px-5 py-3 text-foreground-600">{ren.servicio}</td>
                  <td className="px-5 py-3 text-center">
                    <span className="text-foreground-600">{new Date(ren.fechaRenovacion + 'T00:00:00').toLocaleDateString('es-MX')}</span>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <Badge variant={dias <= 2 ? 'danger' : dias <= 5 ? 'warning' : 'info'}>
                      {dias <= 1 ? 'Mañana' : `${dias} días`}
                    </Badge>
                  </td>
                </tr>
              );
            })}
            {renovaciones.length === 0 && (
              <tr>
                <td colSpan={4} className="px-5 py-8 text-center text-sm text-foreground-500">
                  No hay renovaciones en los próximos 7 días
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}